import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { Loader2, CheckCircle2, Clock, ShieldAlert, RefreshCw } from "lucide-react";
import { refreshMembership } from "@/lib/membership-refresh";
import { trackFunnel } from "@/lib/funnel";
import { readSelectedPlanId } from "@/lib/selected-plan";
import { DigitalConsentSummary } from "@/components/membership/DigitalConsentSummary";

import {
  getMyPaypalSubscription,
  syncMyPaypalSubscription,
} from "@/lib/paypal-subscriptions.functions";

// User is returned here by PayPal after approving the subscription.
// Flow:
//   1. On mount, immediately call syncMyPaypalSubscription — this fetches the
//      subscription LIVE from PayPal and writes ACTIVE + memberships row in
//      one round-trip, so we don't have to wait for the webhook.
//   2. Poll our own DB every 2s for up to ~15s as a belt-and-braces check
//      (e.g. if the live GET returned APPROVAL_PENDING for a split second).
//   3. If still not active after the timeout, show the "still processing"
//      screen with a Refresh Status button and a safe-to-close message.

export const Route = createFileRoute("/paypal/return")({
  head: () => ({
    meta: [
      { title: "Completing your membership — Recovery Trauma\u2122" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  validateSearch: (search: Record<string, unknown>) =>
    z
      .object({
        subscription_id: z.string().optional(),
        ba_token: z.string().optional(),
        token: z.string().optional(),
      })
      .parse(search),
  component: PaypalReturnPage,
});

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 15000;

type MyRow = null | {
  status: string;
  setup_fee_status: string;
  start_time: string | null;
  next_billing_time: string | null;
  paid_through: string | null;
};

function PaypalReturnPage() {
  const { subscription_id } = Route.useSearch();
  const getMine = useServerFn(getMyPaypalSubscription);
  const syncMine = useServerFn(syncMyPaypalSubscription);
  const navigate = useNavigate();

  const [row, setRow] = useState<MyRow>(null);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<"verifying" | "active" | "pending">("verifying");
  const [refreshing, setRefreshing] = useState(false);
  const startedAtRef = useRef<number>(Date.now());
  const redirectedRef = useRef(false);

  const isActive = (r: MyRow) =>
    !!r && (r.status === "ACTIVE" || r.setup_fee_status === "paid");

  useEffect(() => {
    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;

    const readOwnRow = async (): Promise<MyRow> => {
      const r = (await getMine({
        data: subscription_id ? { subscription_id } : {},
      } as never)) as MyRow;
      return r;
    };

    const finish = (r: MyRow) => {
      if (cancelled) return;
      setRow(r);
      setPhase("active");
      // Funnel step — PayPal payment confirmed, attributed to the chosen plan.
      trackFunnel("payment_completed", {
        source: "paypal_return",
        provider: "paypal",
        plan: readSelectedPlanId() ?? undefined,
        dedupeKey: subscription_id ?? "paypal_no_ref",
      });
      if (redirectedRef.current) return;
      redirectedRef.current = true;
      // Tell the client membership cache to re-hydrate immediately, so the
      // member lands on /youre-in already recognised as paid (no flash back
      // to the checkout path while the stale cache expires).
      refreshMembership();

      // Small pause so the success screen is briefly visible.
      setTimeout(() => {
        if (!cancelled) navigate({ to: "/youre-in" });
      }, 900);
    };

    const poll = async () => {
      try {
        const r = await readOwnRow();
        if (cancelled) return;
        setRow(r);
        if (isActive(r)) {
          finish(r);
          return;
        }
        if (Date.now() - startedAtRef.current >= POLL_TIMEOUT_MS) {
          setPhase("pending");
          return;
        }
        pollTimer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        console.error("[paypal-return] poll failed", e);
        // Don't fail hard — poll will retry until timeout, then show pending.
        if (Date.now() - startedAtRef.current >= POLL_TIMEOUT_MS) {
          setPhase("pending");
        } else {
          pollTimer = setTimeout(poll, POLL_INTERVAL_MS);
        }
      }
    };

    const run = async () => {
      console.info("[paypal-return] mounted", { subscription_id });
      // 1. Live sync — the fast path.
      if (subscription_id) {
        try {
          const synced = await syncMine({ data: { subscription_id } } as never);
          console.info("[paypal-return] sync result", synced);
          if (!cancelled && synced && "activated" in synced && synced.activated) {
            const r = await readOwnRow();
            if (isActive(r)) {
              finish(r);
              return;
            }
          }
        } catch (e) {
          console.warn("[paypal-return] sync threw, falling back to polling", e);
        }
      }
      // 2. Poll our DB as fallback (webhook path).
      if (!cancelled) poll();
    };

    run();
    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
    // Intentionally excludes navigate — server-fn refs are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription_id]);

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    setError(null);
    try {
      if (subscription_id) {
        try {
          await syncMine({ data: { subscription_id } } as never);
        } catch (e) {
          console.warn("[paypal-return] manual sync failed", e);
        }
      }
      const r = (await getMine({
        data: subscription_id ? { subscription_id } : {},
      } as never)) as MyRow;
      setRow(r);
      if (isActive(r)) {
        setPhase("active");
        redirectedRef.current = true;
        setTimeout(() => navigate({ to: "/youre-in" }), 500);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-[80vh] max-w-md flex-col items-center justify-center gap-6 p-6 text-center">
      {error ? (
        <>
          <ShieldAlert className="h-10 w-10 text-destructive" />
          <h1 className="font-display text-2xl">We couldn't confirm your subscription</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
          <Link to="/complete-membership" className="btn-gold">
            Pay by card instead
          </Link>
          <Link to="/membership" className="text-xs text-muted-foreground underline">
            Back to membership
          </Link>
        </>
      ) : phase === "active" ? (
        <>
          <CheckCircle2 className="h-12 w-12 text-[var(--gold-deep)]" />
          <h1 className="font-display text-2xl">Thank you — your PayPal payment is verified</h1>
          <p className="text-sm text-muted-foreground">
            Your membership is now active. Your next recurring charge will be on{" "}
            <strong>
              {row?.next_billing_time
                ? new Date(row.next_billing_time).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                : "1 September 2026"}
            </strong>
            .
          </p>
          <DigitalConsentSummary />
          <button
            type="button"
            onClick={() => navigate({ to: "/youre-in" })}
            className="btn-gold"
          >
            Continue to your welcome page
          </button>
        </>
      ) : phase === "pending" ? (
        <>
          <Clock className="h-10 w-10 text-[var(--gold-deep)]" />
          <h1 className="font-display text-2xl">Your payment is being verified</h1>
          <p className="text-sm text-muted-foreground">
            This is taking a little longer than expected, but your payment is still being processed.
          </p>
          <p className="text-sm text-muted-foreground">
            You can safely close this page. We'll activate your membership automatically and
            send you a confirmation email as soon as verification is complete.
          </p>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-2 rounded-full border border-[var(--gold-deep)]/40 px-5 py-2 text-sm font-medium text-[var(--gold-deep)] hover:bg-[var(--gold-deep)]/5 disabled:opacity-60"
          >
            {refreshing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh status
          </button>
          <Link
            to="/complete-membership"
            className="text-sm font-medium text-[var(--gold-deep)] underline-offset-4 hover:underline"
          >
            Prefer to pay by card? Use card checkout instead
          </Link>
          <Link to="/membership" className="text-xs text-muted-foreground underline">
            Back to membership
          </Link>
        </>
      ) : (
        <>
          <Clock className="h-10 w-10 text-[var(--gold-deep)]" />
          <h1 className="font-display text-2xl">Finalising your subscription…</h1>
          <p className="text-sm text-muted-foreground">
            We're verifying your PayPal payment. This usually takes only a few seconds —
            you do not need to pay again.
          </p>
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </>
      )}
    </div>
  );
}
